/**
 * ChecklistRow — Two-line checklist item row
 *
 * Line 1: Status icon + title + required badge + status badge
 * Line 2: Clause ref pills + description preview + linked document count
 *
 * Single click opens the detail sheet directly (no expand/collapse).
 */

import { motion } from "framer-motion";
import {
  CheckCircle2,
  Circle,
  Loader,
  FileText,
  MessageSquare,
} from "lucide-react";
import { Badge } from "@/components/ui";
import { useTranslation } from "@/packages/utils";
import { ClauseRefBadge } from "./ClauseRefBadge";
import type { UIChecklistItem, ChecklistItemStatus } from "@/packages/types";

// ─── Status Config ──────────────────────────────────────────────────────────

export type ChecklistStatusConfig = {
  labelKey: string;
  dotColor: string;
  badgeBg: string;
  badgeText: string;
  /** Resolved label — populated at render time via t() */
  label: string;
};

const CHECKLIST_STATUS_CONFIG_BASE: Record<
  ChecklistItemStatus,
  Omit<ChecklistStatusConfig, "label"> & { label: string }
> = {
  MISSING: {
    labelKey: "checklist.status.missing",
    label: "Missing",
    dotColor: "#6C6C70",
    badgeBg: "rgba(108,108,112,0.15)",
    badgeText: "#A1A1A6",
  },
  UPLOADED: {
    labelKey: "checklist.status.uploaded",
    label: "Uploaded",
    dotColor: "#E8734A",
    badgeBg: "rgba(232,115,74,0.15)",
    badgeText: "#E8734A",
  },
  APPROVED: {
    labelKey: "checklist.status.approved",
    label: "Approved",
    dotColor: "#22c55e",
    badgeBg: "rgba(34,197,94,0.15)",
    badgeText: "#22c55e",
  },
};

/**
 * Non-reactive config — used by external components that already have their own t().
 * For translated labels, use `useChecklistStatusConfig()` hook instead.
 */
export const CHECKLIST_STATUS_CONFIG = CHECKLIST_STATUS_CONFIG_BASE;

/**
 * Hook that returns status config with translated labels.
 */
export function useChecklistStatusConfig(): Record<
  ChecklistItemStatus,
  ChecklistStatusConfig
> {
  const { t } = useTranslation("audits");
  return {
    MISSING: {
      ...CHECKLIST_STATUS_CONFIG_BASE.MISSING,
      label: t(
        CHECKLIST_STATUS_CONFIG_BASE.MISSING
          .labelKey as "checklist.status.missing",
      ) as string,
    },
    UPLOADED: {
      ...CHECKLIST_STATUS_CONFIG_BASE.UPLOADED,
      label: t(
        CHECKLIST_STATUS_CONFIG_BASE.UPLOADED
          .labelKey as "checklist.status.uploaded",
      ) as string,
    },
    APPROVED: {
      ...CHECKLIST_STATUS_CONFIG_BASE.APPROVED,
      label: t(
        CHECKLIST_STATUS_CONFIG_BASE.APPROVED
          .labelKey as "checklist.status.approved",
      ) as string,
    },
  };
}

// ─── Status Icon ────────────────────────────────────────────────────────────

function StatusIcon({ status }: { status: ChecklistItemStatus }) {
  switch (status) {
    case "APPROVED":
      return (
        <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-500/10">
          <CheckCircle2 size={14} className="text-emerald-500" />
        </div>
      );
    case "UPLOADED":
      return (
        <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent-soft">
          <Loader size={14} className="text-accent" />
        </div>
      );
    default:
      return (
        <div className="flex h-5 w-5 shrink-0 items-center justify-center">
          <Circle size={14} className="text-muted" />
        </div>
      );
  }
}

// ─── Checklist Row ──────────────────────────────────────────────────────────

export interface ChecklistRowProps {
  item: UIChecklistItem;
  index: number;
  onOpenDetail: () => void;
}

export function ChecklistRow({ item, index, onOpenDetail }: ChecklistRowProps) {
  const { t } = useTranslation("audits");
  const statusConfigs = useChecklistStatusConfig();
  const statusConfig = statusConfigs[item.status];

  const clauseRefs = item.clauseDetails?.map((c) => c.clauseNumber) ?? [];
  const description = item.description ?? item.informationReport ?? null;
  const truncatedDescription = description
    ? description.slice(0, 80) + (description.length > 80 ? "\u2026" : "")
    : null;
  const linkedDocCount = item.linkedDocumentIds?.length ?? 0;
  const docCountLabel =
    linkedDocCount === 0
      ? (t("checklist.row.noDocuments" as "checklist.item.required") as string)
      : linkedDocCount === 1
        ? (t(
            "checklist.row.oneDocument" as "checklist.item.required",
          ) as string)
        : (t("checklist.row.nDocuments" as "checklist.item.required", {
            count: linkedDocCount,
          }) as string);

  const totalQuestions = item.questions?.length ?? 0;
  const answeredOrders = new Set(
    (item.answers ?? []).map((a) => a.question.order),
  );
  const answeredQuestions = answeredOrders.size;

  const hasSecondLine =
    clauseRefs.length > 0 ||
    truncatedDescription ||
    linkedDocCount > 0 ||
    totalQuestions > 0;

  return (
    <motion.div
      className="rounded-xl bg-card border-l-2 border-l-transparent hover:border-l-accent cursor-pointer transition-colors"
      initial={{ opacity: 0, x: -6 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.03 }}
      onClick={(e) => {
        // Don't open detail sheet when clicking clause ref popovers
        if ((e.target as HTMLElement).closest("button[data-clause-ref]"))
          return;
        onOpenDetail();
      }}
    >
      <div className="px-4 py-3 flex flex-col gap-1">
        {/* Line 1: status icon + title + badges */}
        <div className="flex items-center gap-3">
          <StatusIcon status={item.status} />
          <p
            className={`text-sm font-semibold flex-1 truncate ${
              item.status === "APPROVED"
                ? "text-secondary line-through"
                : "text-primary"
            }`}
          >
            {item.name}
          </p>
          <div className="flex items-center gap-2 shrink-0">
            {item.required && (
              <Badge
                color={{ bg: "rgba(239,68,68,0.1)", text: "#ef4444" }}
                className="px-2 py-0.5 text-[10px]"
              >
                {t("checklist.item.required")}
              </Badge>
            )}
            <Badge
              color={{
                bg: statusConfig.badgeBg,
                text: statusConfig.badgeText,
              }}
              className="w-[84px] text-center"
            >
              {statusConfig.label}
            </Badge>
          </div>
        </div>

        {/* Line 2: clause refs + description + doc count */}
        {hasSecondLine && (
          <div className="flex items-center gap-2 pl-8 flex-wrap">
            {clauseRefs.map((ref) => {
              const detail = item.clauseDetails?.find(
                (c) => c.clauseNumber === ref,
              );
              return (
                <ClauseRefBadge
                  key={ref}
                  clauseRef={ref}
                  clauseInfo={
                    detail
                      ? {
                          standard: ref.split(" ")[0] ?? "",
                          title: detail.title,
                          description: detail.description ?? "",
                        }
                      : undefined
                  }
                />
              );
            })}
            {truncatedDescription && (
              <>
                {clauseRefs.length > 0 && (
                  <span className="text-muted text-[11px]">&middot;</span>
                )}
                <span className="text-xs text-muted-foreground truncate max-w-[280px]">
                  {truncatedDescription}
                </span>
              </>
            )}
            {totalQuestions > 0 && (
              <span className="flex items-center gap-1.5 shrink-0">
                <MessageSquare size={11} className="text-muted-foreground" />
                <div className="h-1 w-12 bg-border/50 rounded-full overflow-hidden">
                  <motion.div
                    className="h-full rounded-full bg-accent"
                    initial={{ width: 0 }}
                    animate={{
                      width: `${Math.round((answeredQuestions / totalQuestions) * 100)}%`,
                    }}
                    transition={{ duration: 0.4, ease: "easeOut" }}
                  />
                </div>
                <span className="text-[11px] text-muted tabular-nums">
                  {answeredQuestions}/{totalQuestions}
                </span>
              </span>
            )}
            <span className="text-xs text-muted-foreground flex items-center gap-1 ml-auto shrink-0">
              <FileText size={11} />
              {docCountLabel}
            </span>
          </div>
        )}
      </div>
    </motion.div>
  );
}
